import type { ReportLayoutWidgetDto, ReportTemplateFlowSectionDto } from "../../api/reportTemplateService"
import { TABLE_PREVIEW_HEADER_HEIGHT, TABLE_PREVIEW_MIN_ROW_HEIGHT, TABLE_PREVIEW_RENDER_BUFFER, clamp, estimateTableRowHeights, getWidgetFlowOrder, getWidgetFlowSectionId, getWidgetManualPage, getWidgetPageBreakBefore, getWidgetPositionMode, toFiniteNumber } from "./editorUtils"
import type { PreviewBodyRenderEntry } from "./layoutTypes"

type BuildPreviewBodyPageEntriesParams = {
  baseBodyCanvasHeight: number
  flowSections: ReportTemplateFlowSectionDto[]
  manualPageCount: number
  previewBodyWidgets: ReportLayoutWidgetDto[]
  primaryFlowSectionId: string
  getTablePreviewRows: (widget: ReportLayoutWidgetDto, columns: string[]) => string[][]
  getWidgetMeasuredHeight?: (widget: ReportLayoutWidgetDto) => number | undefined
}

export function buildPreviewBodyPageEntries({
  baseBodyCanvasHeight,
  flowSections,
  manualPageCount,
  previewBodyWidgets,
  primaryFlowSectionId,
  getTablePreviewRows,
  getWidgetMeasuredHeight,
}: BuildPreviewBodyPageEntriesParams): Map<number, PreviewBodyRenderEntry[]> {
  const pageMap = new Map<number, PreviewBodyRenderEntry[]>()
  const pageBottomMap = new Map<number, number>()
  const sectionPlacementMap = new Map<string, { pageIndex: number; startY: number }>()
  const bodyHeight = Math.max(200, baseBodyCanvasHeight)
  const tableHeaderHeight = TABLE_PREVIEW_HEADER_HEIGHT
  const tableRowHeight = TABLE_PREVIEW_MIN_ROW_HEIGHT
  const flowGap = 0
  const continuationTop = 0
  const tableBottomPadding = TABLE_PREVIEW_RENDER_BUFFER
  const maxFlowGap = 48

  const pushEntry = (pageIndex: number, entry: PreviewBodyRenderEntry, bottom: number) => {
    const current = pageMap.get(pageIndex) ?? []
    current.push(entry)
    pageMap.set(pageIndex, current)
    pageBottomMap.set(pageIndex, Math.max(pageBottomMap.get(pageIndex) ?? 0, bottom))
  }

  const getFlowStartY = (pageIndex: number, anchorMinY: number, minGap: number) => {
    const occupiedBottom = pageBottomMap.get(pageIndex) ?? 0
    const withGap = occupiedBottom > 0 ? occupiedBottom + minGap : 0
    return Math.max(anchorMinY, withGap)
  }
  const getSectionPlacementKey = (manualPageIndex: number, sectionId: string) => `${manualPageIndex}:${sectionId}`

  const toManualPageIndex = (widget: ReportLayoutWidgetDto) =>
    clamp(getWidgetManualPage(widget, 1), 1, manualPageCount) - 1
  const sectionOrderMap = new Map<string, number>()
  const sectionBreakModeMap = new Map<string, "flow" | "newPage">()
  const sectionMinHeightMap = new Map<string, number>()
  flowSections.forEach((section, index) => {
    sectionOrderMap.set(section.sectionId, index + 1)
    sectionBreakModeMap.set(section.sectionId, section.breakMode === "newPage" ? "newPage" : "flow")
    sectionMinHeightMap.set(section.sectionId, Math.max(20, Math.floor(Number(section.minHeight) || 140)))
  })
  const getSectionId = (widget: ReportLayoutWidgetDto) => {
    const sectionId = getWidgetFlowSectionId(widget, primaryFlowSectionId)
    return sectionOrderMap.has(sectionId) ? sectionId : primaryFlowSectionId
  }
  const sectionTopOffsetMap = new Map<string, number>()
  previewBodyWidgets.forEach((widget) => {
    const key = getSectionPlacementKey(toManualPageIndex(widget), getSectionId(widget))
    const widgetY = Math.max(0, toFiniteNumber(widget.y, 0))
    const currentMin = sectionTopOffsetMap.get(key)
    if (currentMin == null || widgetY < currentMin) {
      sectionTopOffsetMap.set(key, widgetY)
    }
  })
  const getSectionRelativeY = (widget: ReportLayoutWidgetDto) => {
    const key = getSectionPlacementKey(toManualPageIndex(widget), getSectionId(widget))
    const sectionTopOffset = sectionTopOffsetMap.get(key) ?? 0
    return Math.max(0, toFiniteNumber(widget.y, 0) - sectionTopOffset)
  }
  const isWorkerTableWidget = (widget: ReportLayoutWidgetDto) =>
    widget.type === "table" && String(widget.props?.tableSource || "reportRows") === "workers"
  const getWorkerTableSplitSafetyPadding = (widget: ReportLayoutWidgetDto) =>
    isWorkerTableWidget(widget) ? 12 : 0
  const buildChunkedWorkerTableEntries = (
    widget: ReportLayoutWidgetDto,
    columns: string[],
    allRows: string[][],
    fallbackRow: string[],
    initialPageIndex: number,
    initialStartY: number,
    firstPlacementGap: number,
  ) => {
    const entries: Array<{
      pageIndex: number
      renderY: number
      renderHeight: number
      tableRows: string[][]
      tableRowHeights: number[]
    }> = []

    let pageIndex = initialPageIndex
    let cursor = 0
    let firstChunk = true

    while (cursor < allRows.length) {
      const gapForChunk = firstChunk ? firstPlacementGap : flowGap
      const startY = firstChunk
        ? initialStartY
        : getFlowStartY(pageIndex, continuationTop, gapForChunk)

      const minTableHeight = tableHeaderHeight + tableRowHeight
      if (startY + minTableHeight > bodyHeight) {
        pageIndex += 1
        firstChunk = false
        continue
      }

      const availableHeight = Math.max(
        tableRowHeight,
        bodyHeight - startY - tableHeaderHeight - tableBottomPadding - getWorkerTableSplitSafetyPadding(widget),
      )
      const remainingRows = allRows.slice(cursor)
      const estimatedRowHeights = estimateTableRowHeights(widget, columns, remainingRows)
      const effectiveRows: string[][] = []
      const effectiveRowHeights: number[] = []
      let consumedHeight = 0

      for (let rowIndex = 0; rowIndex < remainingRows.length; rowIndex += 1) {
        const nextRow = remainingRows[rowIndex]
        const nextHeight = estimatedRowHeights[rowIndex] ?? tableRowHeight
        if (effectiveRows.length > 0 && consumedHeight + nextHeight > availableHeight) {
          break
        }
        effectiveRows.push(nextRow)
        effectiveRowHeights.push(nextHeight)
        consumedHeight += nextHeight
        if (consumedHeight >= availableHeight) {
          break
        }
      }

      if (effectiveRows.length === 0) {
        effectiveRows.push(fallbackRow)
        effectiveRowHeights.push(tableRowHeight)
        consumedHeight = tableRowHeight
      }

      entries.push({
        pageIndex,
        renderY: startY,
        renderHeight: tableHeaderHeight + consumedHeight + tableBottomPadding,
        tableRows: effectiveRows,
        tableRowHeights: effectiveRowHeights,
      })

      cursor += effectiveRows.length
      if (cursor < allRows.length) {
        pageIndex += 1
        firstChunk = false
      }
    }

    return entries
  }

  const flowSortedWidgets = [...previewBodyWidgets].sort((a, b) => {
    const pageDiff = toManualPageIndex(a) - toManualPageIndex(b)
    if (pageDiff !== 0) return pageDiff
    const sectionDiff = (sectionOrderMap.get(getSectionId(a)) ?? 1) - (sectionOrderMap.get(getSectionId(b)) ?? 1)
    if (sectionDiff !== 0) return sectionDiff
    const orderDiff = getWidgetFlowOrder(a, 1) - getWidgetFlowOrder(b, 1)
    if (orderDiff !== 0) return orderDiff
    const yDiff = getSectionRelativeY(a) - getSectionRelativeY(b)
    if (yDiff !== 0) return yDiff
    return (a.zIndex || 0) - (b.zIndex || 0)
  })
  const flowSequence = flowSortedWidgets.filter((widget) => getWidgetPositionMode(widget) === "flow")
  const flowGapByWidgetId = new Map<string, number>()
  const getEffectiveWidgetHeight = (widget: ReportLayoutWidgetDto) => {
    const measuredHeight = getWidgetMeasuredHeight?.(widget)
    return clamp(
      Math.round(toFiniteNumber(measuredHeight, toFiniteNumber(widget.height, 56))),
      10,
      bodyHeight,
    )
  }
  flowSequence.forEach((widget, index) => {
    const currentPage = toManualPageIndex(widget)
    const currentSectionId = getSectionId(widget)
    const breakBeforeCurrent = getWidgetPageBreakBefore(widget)
    if (index === 0) {
      flowGapByWidgetId.set(widget.id, flowGap)
      return
    }
    if (breakBeforeCurrent) {
      flowGapByWidgetId.set(widget.id, flowGap)
      return
    }
    const prev = flowSequence[index - 1]
    const prevPage = toManualPageIndex(prev)
    const prevSectionId = getSectionId(prev)
    if (prevPage !== currentPage) {
      flowGapByWidgetId.set(widget.id, flowGap)
      return
    }
    if (prevSectionId !== currentSectionId) {
      flowGapByWidgetId.set(widget.id, flowGap)
      return
    }
    const prevBottom = getSectionRelativeY(prev) + getEffectiveWidgetHeight(prev)
    const designedGap = Math.max(0, getSectionRelativeY(widget) - prevBottom)
    const normalizedGap =
      prev.type === "table" || widget.type === "table"
        ? flowGap
        : clamp(designedGap, flowGap, maxFlowGap)
    flowGapByWidgetId.set(widget.id, normalizedGap)
  })

  let activeFlowPage = 0
  let lastFlowSectionId: string | null = null
  let lastFlowManualPageIndex = -1
  let pageOverflowCarry = 0
  const actualBasePageByDesignPage = new Map<number, number>()
  let reservedSectionPlacementKey: string | null = null
  let reservedSectionPageIndex = -1
  let reservedSectionBottom = 0

  const finalizeReservedSectionBottom = () => {
    if (reservedSectionPlacementKey == null || reservedSectionPageIndex < 0) return
    pageBottomMap.set(
      reservedSectionPageIndex,
      Math.max(pageBottomMap.get(reservedSectionPageIndex) ?? 0, reservedSectionBottom),
    )
    reservedSectionPlacementKey = null
    reservedSectionPageIndex = -1
    reservedSectionBottom = 0
  }

  const beginReservedSection = (
    placementKey: string,
    pageIndex: number,
    sectionStartY: number,
    sectionId: string,
  ) => {
    if (reservedSectionPlacementKey !== placementKey) {
      finalizeReservedSectionBottom()
      reservedSectionPlacementKey = placementKey
      reservedSectionPageIndex = pageIndex
      reservedSectionBottom = sectionStartY + (sectionMinHeightMap.get(sectionId) ?? 0)
      return
    }
    reservedSectionPageIndex = pageIndex
    reservedSectionBottom = Math.max(
      reservedSectionBottom,
      sectionStartY + (sectionMinHeightMap.get(sectionId) ?? 0),
    )
  }

  const extendReservedSectionBottom = (
    placementKey: string,
    pageIndex: number,
    sectionStartY: number,
    sectionId: string,
    contentBottom: number,
  ) => {
    beginReservedSection(placementKey, pageIndex, sectionStartY, sectionId)
    reservedSectionPageIndex = pageIndex
    reservedSectionBottom = Math.max(
      reservedSectionBottom,
      contentBottom,
      sectionStartY + (sectionMinHeightMap.get(sectionId) ?? 0),
    )
  }

  flowSortedWidgets.forEach((widget, index) => {
    const positionMode = getWidgetPositionMode(widget)
    const placementGap = flowGapByWidgetId.get(widget.id) ?? flowGap
    const designWidgetBasePage = toManualPageIndex(widget)
    if (!actualBasePageByDesignPage.has(designWidgetBasePage)) {
      actualBasePageByDesignPage.set(designWidgetBasePage, designWidgetBasePage + pageOverflowCarry)
    }
    const widgetBasePage = actualBasePageByDesignPage.get(designWidgetBasePage) ?? designWidgetBasePage
    const pageBreakBefore = getWidgetPageBreakBefore(widget)
    const sectionId = getSectionId(widget)
    const sectionBreakMode = sectionBreakModeMap.get(sectionId) ?? "flow"
    const sectionChanged = sectionId !== lastFlowSectionId || designWidgetBasePage !== lastFlowManualPageIndex
    const keepDesignAnchorOnBreak = pageBreakBefore
    const safeHeight = getEffectiveWidgetHeight(widget)
    const firstPageAnchor = clamp(
      getSectionRelativeY(widget),
      0,
      Math.max(0, bodyHeight - safeHeight),
    )
    const sectionPlacementKey = getSectionPlacementKey(designWidgetBasePage, sectionId)
    if (reservedSectionPlacementKey && reservedSectionPlacementKey !== sectionPlacementKey) {
      finalizeReservedSectionBottom()
    }
    const currentSectionPlacement = sectionPlacementMap.get(sectionPlacementKey)

    if (positionMode === "absolute") {
      let pageIndex = currentSectionPlacement?.pageIndex ?? Math.max(activeFlowPage, widgetBasePage)
      if (!currentSectionPlacement) {
        if (sectionChanged && sectionBreakMode === "newPage" && (pageBottomMap.get(pageIndex) ?? 0) > 0) {
          pageIndex += 1
        }
        if (pageBreakBefore && (pageBottomMap.get(pageIndex) ?? 0) > 0) {
          pageIndex += 1
        }
      }
      const sectionStartY =
        currentSectionPlacement?.startY ??
        getFlowStartY(
          pageIndex,
          pageIndex === widgetBasePage ? 0 : continuationTop,
          placementGap,
        )
      const renderY = clamp(sectionStartY + firstPageAnchor, 0, Math.max(0, bodyHeight - safeHeight))
      if (widget.type === "table") {
        const columns = String(widget.props?.columns || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
        const allRowsRaw = getTablePreviewRows(widget, columns)
        const fallbackRow = columns.length > 0 ? columns.map(() => "-") : ["-"]
        const tableRows = allRowsRaw.length > 0 ? allRowsRaw : [fallbackRow]
        if (isWorkerTableWidget(widget)) {
          const chunkedEntries = buildChunkedWorkerTableEntries(
            widget,
            columns,
            tableRows,
            fallbackRow,
            pageIndex,
            renderY,
            placementGap,
          )
          chunkedEntries.forEach((entry) => {
            pushEntry(
              entry.pageIndex,
              {
                widget,
                renderY: entry.renderY,
                renderHeight: entry.renderHeight,
                tableRows: entry.tableRows,
                tableRowHeights: entry.tableRowHeights,
              },
              entry.renderY + entry.renderHeight,
            )
          })
          const lastEntry = chunkedEntries[chunkedEntries.length - 1]
          if (lastEntry) {
            extendReservedSectionBottom(
              sectionPlacementKey,
              lastEntry.pageIndex,
              sectionStartY,
              sectionId,
              lastEntry.renderY + lastEntry.renderHeight,
            )
            sectionPlacementMap.set(sectionPlacementKey, {
              pageIndex,
              startY: sectionStartY,
            })
            pageOverflowCarry = Math.max(pageOverflowCarry, lastEntry.pageIndex - designWidgetBasePage)
            activeFlowPage = Math.max(activeFlowPage, pageIndex)
            lastFlowSectionId = sectionId
            lastFlowManualPageIndex = designWidgetBasePage
            return
          }
        }
        const tableRowHeights = isWorkerTableWidget(widget)
          ? estimateTableRowHeights(widget, columns, tableRows)
          : undefined
        pushEntry(
          pageIndex,
          { widget, renderY, renderHeight: safeHeight, tableRows, tableRowHeights },
          renderY + safeHeight,
        )
      } else {
        pushEntry(pageIndex, { widget, renderY, renderHeight: safeHeight }, renderY + safeHeight)
      }
      extendReservedSectionBottom(sectionPlacementKey, pageIndex, sectionStartY, sectionId, renderY + safeHeight)
      sectionPlacementMap.set(sectionPlacementKey, { pageIndex, startY: sectionStartY })
      activeFlowPage = Math.max(activeFlowPage, pageIndex)
      lastFlowSectionId = sectionId
      lastFlowManualPageIndex = designWidgetBasePage
      return
    }

    if (widget.type !== "table") {
      let pageIndex = Math.max(activeFlowPage, widgetBasePage)
      if (sectionChanged && sectionBreakMode === "newPage" && (pageBottomMap.get(pageIndex) ?? 0) > 0) {
        pageIndex += 1
      }
      if (pageBreakBefore && (pageBottomMap.get(pageIndex) ?? 0) > 0) {
        pageIndex += 1
      }
      const anchorForPage =
        keepDesignAnchorOnBreak
          ? firstPageAnchor
          : (pageIndex === widgetBasePage ? firstPageAnchor : continuationTop)
      let startY = getFlowStartY(pageIndex, anchorForPage, placementGap)
      if (startY + safeHeight > bodyHeight) {
        if (keepDesignAnchorOnBreak) {
          const fallbackStartY = getFlowStartY(pageIndex, continuationTop, placementGap)
          if (fallbackStartY + safeHeight <= bodyHeight) {
            startY = fallbackStartY
          } else {
            pageIndex += 1
            startY = getFlowStartY(pageIndex, continuationTop, placementGap)
          }
        } else {
          pageIndex += 1
          startY = getFlowStartY(pageIndex, continuationTop, placementGap)
        }
      }
      if (startY + safeHeight > bodyHeight) {
        startY = 0
      }

      pushEntry(pageIndex, { widget, renderY: startY, renderHeight: safeHeight }, startY + safeHeight)
      const sectionStartY = currentSectionPlacement?.startY ?? Math.max(0, startY - firstPageAnchor)
      extendReservedSectionBottom(sectionPlacementKey, pageIndex, sectionStartY, sectionId, startY + safeHeight)
      if (!currentSectionPlacement) {
        sectionPlacementMap.set(sectionPlacementKey, {
          pageIndex,
          startY: Math.max(0, startY - firstPageAnchor),
        })
      }
      activeFlowPage = Math.max(activeFlowPage, pageIndex)
      lastFlowSectionId = sectionId
      lastFlowManualPageIndex = designWidgetBasePage
      return
    }

    const columns = String(widget.props?.columns || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)

    const allRowsRaw = getTablePreviewRows(widget, columns)
    const fallbackRow = columns.length > 0 ? columns.map(() => "-") : ["-"]
    const allRows = allRowsRaw.length > 0 ? allRowsRaw : [fallbackRow]

    let pageIndex = Math.max(activeFlowPage, widgetBasePage)
    if (sectionChanged && sectionBreakMode === "newPage" && (pageBottomMap.get(pageIndex) ?? 0) > 0) {
      pageIndex += 1
    }
    if (pageBreakBefore && (pageBottomMap.get(pageIndex) ?? 0) > 0) {
      pageIndex += 1
    }
    let cursor = 0
    let firstChunk = true

    while (cursor < allRows.length) {
      const anchor =
        firstChunk
          ? (keepDesignAnchorOnBreak ? firstPageAnchor : (pageIndex === widgetBasePage ? firstPageAnchor : continuationTop))
          : continuationTop
      const gapForChunk = firstChunk ? placementGap : flowGap
      let startY = getFlowStartY(pageIndex, anchor, gapForChunk)

      const minTableHeight = tableHeaderHeight + tableRowHeight
      if (startY + minTableHeight > bodyHeight) {
        if (firstChunk && keepDesignAnchorOnBreak) {
          const fallbackStartY = getFlowStartY(pageIndex, continuationTop, gapForChunk)
          if (fallbackStartY + minTableHeight <= bodyHeight) {
            startY = fallbackStartY
          } else {
            pageIndex += 1
            firstChunk = false
            continue
          }
        } else {
          pageIndex += 1
          firstChunk = false
          continue
        }
      }

      const availableHeight = Math.max(
        tableRowHeight,
        bodyHeight - startY - tableHeaderHeight - tableBottomPadding - getWorkerTableSplitSafetyPadding(widget),
      )
      const remainingRows = allRows.slice(cursor)
      const estimatedRowHeights = estimateTableRowHeights(widget, columns, remainingRows)
      const effectiveRows: string[][] = []
      const effectiveRowHeights: number[] = []
      let consumedHeight = 0

      for (let rowIndex = 0; rowIndex < remainingRows.length; rowIndex += 1) {
        const nextRow = remainingRows[rowIndex]
        const nextHeight = estimatedRowHeights[rowIndex] ?? tableRowHeight
        if (effectiveRows.length > 0 && consumedHeight + nextHeight > availableHeight) {
          break
        }
        effectiveRows.push(nextRow)
        effectiveRowHeights.push(nextHeight)
        consumedHeight += nextHeight
        if (consumedHeight >= availableHeight) {
          break
        }
      }

      if (effectiveRows.length === 0) {
        effectiveRows.push(fallbackRow)
        effectiveRowHeights.push(tableRowHeight)
        consumedHeight = tableRowHeight
      }

      const entryBottom = startY + tableHeaderHeight + consumedHeight + tableBottomPadding
      pushEntry(
        pageIndex,
        {
          widget,
          renderY: startY,
          tableRows: effectiveRows,
          tableRowHeights: isWorkerTableWidget(widget) ? effectiveRowHeights : undefined,
        },
        entryBottom,
      )
      const sectionStartY =
        currentSectionPlacement?.startY ??
        Math.max(0, startY - firstPageAnchor)
      extendReservedSectionBottom(sectionPlacementKey, pageIndex, sectionStartY, sectionId, entryBottom)
      if (firstChunk && !currentSectionPlacement) {
        sectionPlacementMap.set(sectionPlacementKey, {
          pageIndex,
          startY: Math.max(0, startY - firstPageAnchor),
        })
      }

      cursor += effectiveRows.length
      if (cursor < allRows.length) {
        pageIndex += 1
        firstChunk = false
      }
    }

    activeFlowPage = Math.max(activeFlowPage, pageIndex)
    lastFlowSectionId = sectionId
    pageOverflowCarry = Math.max(pageOverflowCarry, pageIndex - designWidgetBasePage)
    lastFlowManualPageIndex = designWidgetBasePage
    if (index === flowSortedWidgets.length - 1 && pageMap.size === 0) {
      pageMap.set(0, [])
    }
  })

  if (pageMap.size === 0) {
    pageMap.set(0, [])
  }

  finalizeReservedSectionBottom()

  pageMap.forEach((entries, key) => {
    pageMap.set(
      key,
      [...entries].sort((a, b) => {
        const yDiff = a.renderY - b.renderY
        if (yDiff !== 0) return yDiff
        return (a.widget.zIndex || 0) - (b.widget.zIndex || 0)
      }),
    )
  })

  return pageMap
}

